import React, { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import {
  Rocket,
  Search,
  Target,
  LineChart,
  CheckCircle2,
  BarChart3,
  FileText,
  Gauge,
  Award,
  Laptop2,
  Globe,
  Layers,
  CalendarDays,
  ArrowRight,
  ExternalLink,
  HelpCircle,
  BookOpen,
  Mail,
  MessageSquare,
  Check,
  X,
  ChevronDown,
  ChevronUp,
  Menu as MenuIcon,
  Settings,
  Star,
  HelpCircle as HelpCircleIcon,
  Info,
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Server,
  Cpu,
  Database,
  Cloud,
  Wifi,
  Activity,
  Terminal,
  Code,
  Lock,
  Shield,
  ShieldCheck,
  Key,
  BadgeCheck,
  Phone,
  MessageCircle,
  MessageSquare as MessageSquareIcon,
  Send,
  Bell,
  Bot,
  ShoppingCart,
  Package,
  Truck,
  CreditCard,
  DollarSign,
  Gift,
  MonitorSmartphone,
  Palette,
  Image as ImageIcon,
  Camera,
  Video,
  Mic,
  Lightbulb,
  File,
  Folder,
  Copy,
  Paperclip,
  Briefcase,
  Users,
  Calendar,
  Clock,
  Tag,
  Bookmark,
  Flag,
  GraduationCap,
  TrendingUp,
  BarChart,
  Crown,
  Megaphone,
  Share2,
  Heart,
  Zap,
  ChevronRight,
  Download,
  Upload,
  RefreshCw,
  Repeat,
  MapPin,
  Layout,
  Grid,
  List,
  Filter,
  Home,
} from 'lucide-react';
import { apiUrl } from '../../utils/api';
import { API_CONFIG } from '../../config/api';
import { API_URLS } from '../../config/api';
import { formatPriceWithoutCents } from '../../utils/price';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';
import MediaDisplay from '../MediaDisplay';
import MeetingRequestDialog from '../MeetingRequestDialog';

const whatsappNumber = '905324404011';
const whatsappMessage = encodeURIComponent('Merhaba, Marka tasarım hizmetleriniz hakkında bilgi almak istiyorum.');
const whatsappLink = `https://api.whatsapp.com/send?phone=${whatsappNumber}&text=${whatsappMessage}`;

// Icon mapping for pillars
const iconMap = {
  Gauge: <Gauge className="w-10 h-10 text-indigo-600" />,
  FileText: <FileText className="w-10 h-10 text-indigo-600" />,
  Globe: <Globe className="w-10 h-10 text-indigo-600" />,
  Target: <Target className="w-10 h-10 text-indigo-600" />,
};

// Icon mapping for packages - tüm ikonları desteklemek için
const packageIconMap = {
  // Temel İkonlar
  Home,
  Menu: MenuIcon,
  Settings,
  Search,
  Star,
  
  // Bilgi & Yardım
  HelpCircle: HelpCircleIcon,
  Info,
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  XCircle,
  
  // Teknoloji & Hosting
  Globe,
  Server,
  Cpu,
  Database,
  Cloud,
  Wifi,
  Activity,
  Terminal,
  Code,
  
  // Güvenlik
  Lock,
  Shield,
  ShieldCheck,
  Key,
  BadgeCheck,
  
  // İletişim
  Mail,
  Phone,
  MessageCircle,
  MessageSquare: MessageSquareIcon,
  Send,
  Bell,
  Bot,
  
  // E-ticaret & Finans
  ShoppingCart,
  Package,
  Truck,
  CreditCard,
  DollarSign,
  Gift,
  
  // Tasarım & Medya
  MonitorSmartphone,
  Palette,
  Image: ImageIcon,
  Camera,
  Video,
  Mic,
  Lightbulb,
  
  // İçerik & Belgeler
  FileText,
  BookOpen,
  File,
  Folder,
  Copy,
  Paperclip,
  
  // İş & Organizasyon
  Briefcase,
  Users,
  Calendar,
  Clock,
  Tag,
  Bookmark,
  Flag,
  
  // Eğitim & Büyüme
  GraduationCap,
  Target,
  Rocket,
  TrendingUp,
  BarChart: BarChart3,
  Award,
  Crown,
  LineChart,
  
  // Pazarlama & Sosyal
  Megaphone,
  Share2,
  Heart,
  Zap,
  
  // Navigasyon & Aksiyon
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Download,
  Upload,
  RefreshCw,
  Repeat,
  
  // Görünüm & Düzen
  MapPin,
  Layers,
  Layout,
  Grid,
  List,
  Filter,
};

export default function BrandDesignServicesPage() {
  const [ready, setReady] = useState(false);
  const [pageContent, setPageContent] = useState(null);
  const [packages, setPackages] = useState([]);
  const [seoBlogs, setSeoBlogs] = useState([]);
  const [seoFaqs, setSeoFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [meetingOpen, setMeetingOpen] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setReady(true), 80);
    return () => clearTimeout(timer);
  }, []);

  // Render feature value icon/text
  const renderFeatureValue = (valueType, valueText) => {
    switch (valueType) {
      case 'check':
        return <CheckCircle2 className="w-5 h-5 text-indigo-500 mx-auto" />;
      case 'cross':
        return <X className="w-5 h-5 text-red-500 mx-auto" />;
      case 'optional':
        return <span className="text-xs text-gray-500 mx-auto">Opsiyonel</span>;
      case 'text':
        return <span className="text-xs text-gray-700 mx-auto">{valueText || '-'}</span>;
      default:
        return <X className="w-5 h-5 text-gray-400 mx-auto" />;
    }
  };

  // Normalize image URL helper
  const normalizeImageUrl = (url) => {
    if (!url) return 'https://images.unsplash.com/photo-1520607162513-77705c0f0d4a?auto=format&fit=crop&w=800&q=80';
    const BASE_URL = API_CONFIG.BASE_URL;
    try {
      const u = String(url);
      if (u.startsWith('http://') || u.startsWith('https://')) return u;
      if (u.startsWith('/backend/uploads/')) return `${BASE_URL}${u.replace('/backend', '')}`;
      if (u.startsWith('/uploads/')) return `${BASE_URL}${u}`;
      return `${BASE_URL}/uploads/${u.replace(/^\//, '')}`;
    } catch {
      return 'https://images.unsplash.com/photo-1520607162513-77705c0f0d4a?auto=format&fit=crop&w=800&q=80';
    }
  };

  useEffect(() => {
    // Fetch page content from database
    fetch(apiUrl('/backend/admin_api.php?action=getPageContent&slug=marka-tasarim-hizmeti'))
      .then((res) => {
        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }
        return res.text();
      })
      .then((text) => {
        try {
          const response = JSON.parse(text);
          // Backend returns {status: "success", data: {...}}
          if (response.status === 'success' && response.data) {
            setPageContent(response.data);
          } else {
            console.error('Page content fetch failed:', response.message || 'Unknown error');
          }
        } catch (e) {
          console.error('Error parsing page content JSON:', e);
          console.error('Response text:', text);
        }
      })
      .catch((err) => console.error('Error fetching page content:', err));

    // Packages will be empty for now - can be added later if needed
    setPackages([]);
    setLoading(false);

    // Fetch ilgili blog yazılarını veritabanından çek
    fetch(apiUrl('/backend/api.php?action=getBlogs'))
      .then((res) => res.json())
      .then((data) => {
        if (data.status === 'success') {
          const allowedCategories = ['marka-tasarim-hizmeti'];
          const seoCategory = data.blogs
            .filter(
              (blog) =>
                allowedCategories.includes(blog.category) &&
                blog.is_published === 1
            )
            .slice(0, 3);
          setSeoBlogs(seoCategory);
        }
      })
      .catch((err) => console.error('Error fetching SEO blogs:', err));

    // Fetch SSS'leri veritabanından çek
    // Not: getFAQs endpoint'i faq_categories.title alanına göre filtreliyor (case-insensitive)
    fetch(apiUrl('/backend/api.php?action=getFAQs&category=Marka Tasarım Hizmet'))
      .then((res) => res.json())
      .then((data) => {
        if (data.status === 'success') {
          // FAQs already filtered by category in backend
          setSeoFaqs(data.faqs);
        }
      })
      .catch((err) => console.error('Error fetching SEO FAQs:', err));
  }, []);

  // If page content is not found, use default content
  const defaultPageContent = {
    hero_badge_text: 'Marka Tasarım Hizmeti',
    hero_title: 'Markanızı Güçlendiren Profesyonel Tasarım Çözümleri',
    hero_description: 'Logo tasarımından kurumsal kimlik çalışmalarına, marka stratejisinden görsel iletişim tasarımına kadar markanızı güçlendiren kapsamlı tasarım hizmetleri sunuyoruz.',
    hero_image_url: 'https://images.unsplash.com/photo-1561070791-2526d30994b5?auto=format&fit=crop&w=1100&q=80',
    additional_sections: {}
  };

  const displayPageContent = pageContent || defaultPageContent;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-indigo-50 via-white to-indigo-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  const additionalSections = displayPageContent.additional_sections || {};
  const auditSection = additionalSections.audit_section || {};
  const auditChecklist = additionalSections.audit_checklist?.items || [];
  const seoPillars = additionalSections.pillars || [];
  const roadmapSection = additionalSections.roadmap_section || {};
  const roadmap = additionalSections.roadmap || [];
  const deliverablesSection = additionalSections.deliverables || {};
  const deliverables = deliverablesSection.items || [];
  const caseStudy = additionalSections.case_study || {};
  
  // Use database blogs if available, otherwise fallback to page content
  const seoArticles = seoBlogs.length > 0 ? seoBlogs.map(blog => ({
    title: blog.title,
    excerpt: blog.excerpt,
    link: `/blog/${blog.slug}`,
    tag: blog.level || 'SEO',
    image: normalizeImageUrl(blog.image)
  })) : (additionalSections.articles || []);
  
  // Use database FAQs if available, otherwise fallback to page content
  const displayFaqs = seoFaqs.length > 0 ? seoFaqs.map(faq => ({
    question: faq.question,
    answer: faq.answer
  })) : (additionalSections.faqs || []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-indigo-50 via-white to-indigo-50">
      {/* Hero */}
      <section
        className="relative bg-gradient-to-br from-indigo-600 via-indigo-700 to-indigo-900 py-20 overflow-hidden"
        style={{ opacity: ready ? 1 : 0, transition: 'opacity 0.6s ease-in' }}
      >
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS1vcGFjaXR5PSIwLjEiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')] opacity-10"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row items-center lg:items-start gap-12">
            <div className="w-full lg:w-1/2 text-white text-center lg:text-left">
              <div className="inline-flex">
                <Badge className="bg-white/20 text-white uppercase tracking-widest mb-4">
                  {displayPageContent.hero_badge_text || 'Marka Tasarım Hizmeti'}
                </Badge>
              </div>
              <h1 className="text-4xl md:text-5xl font-extrabold leading-tight mb-6">
                {displayPageContent.hero_title || 'Markanızı Güçlendiren Profesyonel Tasarım Çözümleri'}
              </h1>
              <p className="text-xl text-indigo-100 max-w-2xl mx-auto lg:mx-0">
                {displayPageContent.hero_description || 'Logo tasarımından kurumsal kimlik çalışmalarına, marka stratejisinden görsel iletişim tasarımına kadar markanızı güçlendiren kapsamlı tasarım hizmetleri sunuyoruz.'}
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Button size="lg" className="bg-white text-indigo-700 hover:bg-indigo-100" asChild>
                  <a href="/iletisim" className="inline-flex items-center">
                    <Mail className="w-5 h-5 mr-2" />
                    İletişime Geç
                  </a>
                </Button>
                <Button
                  size="lg"
                  className="bg-emerald-500 text-white hover:bg-emerald-600 shadow-lg border border-emerald-400/70"
                  asChild
                >
                  <a
                    href={whatsappLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center"
                  >
                    <MessageSquare className="w-5 h-5 mr-2" />
                    WhatsApp ile Görüş
                  </a>
                </Button>
                <Button
                  size="lg"
                  className="bg-white text-indigo-900 hover:bg-indigo-50 border border-white/80 shadow-lg"
                  onClick={() => setMeetingOpen(true)}
                >
                  Toplantı Ayarla
                </Button>
              </div>
            </div>
            <div className="w-full lg:w-1/2 flex justify-center">
              <div className="relative w-full max-w-lg">
                <div className="absolute -inset-4 bg-white/20 blur-3xl rounded-full"></div>
                <MediaDisplay
                  src={displayPageContent.hero_image_url || 'https://images.unsplash.com/photo-1561070791-2526d30994b5?auto=format&fit=crop&w=1100&q=80'}
                  alt="Marka tasarım çalışması"
                  className="relative w-full rounded-3xl shadow-2xl object-cover border border-white/10"
                  videoProps={{ controls: true }}
                  imgProps={{ loading: "lazy" }}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section
        className="py-16 bg-gradient-to-b from-white to-indigo-50"
        style={{ opacity: ready ? 1 : 0, transition: 'opacity 0.6s ease-in 0.1s' }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Marka Tasarım Hizmetlerimiz</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Markanızı güçlendiren profesyonel tasarım çözümleri ile görsel kimliğinizi oluşturuyoruz.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Logo Tasarımı */}
            <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 bg-white">
              <CardHeader>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-indigo-600 flex items-center justify-center flex-shrink-0">
                    <Layers className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <CardTitle className="text-xl font-bold text-gray-900 mb-2">Logo Tasarımı</CardTitle>
                    <p className="text-gray-600 leading-relaxed">
                      Markayı temsil eden özgün görsel kimlik çalışmasıdır.
                    </p>
                  </div>
                </div>
              </CardHeader>
            </Card>

            {/* Kartvizit Tasarımı */}
            <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 bg-white">
              <CardHeader>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-indigo-600 flex items-center justify-center flex-shrink-0">
                    <FileText className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <CardTitle className="text-xl font-bold text-gray-900 mb-2">Kartvizit Tasarımı</CardTitle>
                    <p className="text-gray-600 leading-relaxed">
                      Kurumsal iletişim için hazırlanan kart tasarımıdır.
                    </p>
                  </div>
                </div>
              </CardHeader>
            </Card>

            {/* Kurumsal İmaj Yönetimi */}
            <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 bg-white">
              <CardHeader>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-indigo-600 flex items-center justify-center flex-shrink-0">
                    <Settings className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <CardTitle className="text-xl font-bold text-gray-900 mb-2">Kurumsal İmaj Yönetimi</CardTitle>
                    <p className="text-gray-600 leading-relaxed">
                      Markanın tüm görsel duruşunun tutarlı şekilde yönetilmesidir.
                    </p>
                  </div>
                </div>
              </CardHeader>
            </Card>

            {/* Kurumsal Kimlik Tasarımları */}
            <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 bg-white">
              <CardHeader>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-indigo-600 flex items-center justify-center flex-shrink-0">
                    <Award className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <CardTitle className="text-xl font-bold text-gray-900 mb-2">Kurumsal Kimlik Tasarımları</CardTitle>
                    <p className="text-gray-600 leading-relaxed mb-2">
                      Firmanın tüm resmi ve basılı görsellerinin tek kimlikte tasarlanmasıdır.
                    </p>
                    <p className="text-sm text-gray-500 italic">
                      (Sözleşme dosyası, diplomat zarf, antetli kağıt tasarımı vb.)
                    </p>
                  </div>
                </div>
              </CardHeader>
            </Card>
          </div>
        </div>
      </section>

      {/* SEO Packages - styled like hosting page */}
      <section
        className="py-16 bg-white"
        style={{ opacity: ready ? 1 : 0, transition: 'opacity 0.6s ease-in 0.08s' }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Marka Tasarım Hizmet Paketleri</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Markanızın ihtiyaçlarına uygun tasarım paketini seçin, güçlü bir görsel kimlik için birlikte çalışalım.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {packages.map((pkg) => {
              // İkon adını kontrol et ve eşleştir - tüm ikonları destekle
              // Case-insensitive ve trim ile kontrol et
              const iconName = pkg.icon ? String(pkg.icon).trim() : '';
              const Icon = iconName 
                ? (packageIconMap[iconName] || packageIconMap[iconName.charAt(0).toUpperCase() + iconName.slice(1).toLowerCase()] || Rocket)
                : Rocket;
              
              // Parse features from JSON string if needed
              // Önce included_features kontrol et (pricing_plans'dan geliyor)
              let featuresData = pkg.included_features || pkg.features;
              if (typeof featuresData === 'string') {
                try {
                  featuresData = JSON.parse(featuresData);
                } catch (e) {
                  console.error('Error parsing features for package:', pkg.id, e);
                  featuresData = [];
                }
              }
              
              // Eğer features array ise ve içinde string'ler varsa direkt kullan
              // Eğer object'ler varsa name alanını al
              let features = Array.isArray(featuresData)
                ? featuresData.map((f) => {
                    if (typeof f === 'string') {
                      return f;
                    } else if (typeof f === 'object' && f !== null) {
                      // Eğer included field'ı varsa ve false ise atla
                      if (f.included === false) {
                        return null;
                      }
                      return f.name || f.title || f.text || '';
                    }
                    return null;
                  }).filter(f => f !== null && f !== '')
                : [];
              
              // Features are already included from package data

              // Enterprise SEO paketinde özellikler gösterilmesin
              const isEnterprise = pkg.name?.toLowerCase().includes('enterprise') || 
                                   pkg.subtitle?.toLowerCase().includes('enterprise') ||
                                   pkg.slug?.toLowerCase().includes('enterprise');

              return (
                <Card
                  key={pkg.id}
                  className={`relative border-0 shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 cursor-pointer bg-white flex flex-col ${pkg.popular ? 'ring-4 ring-indigo-200' : ''}`}
                >
                  {pkg.popular === 1 && (
                    <div className="absolute top-0 left-1/2 -translate-y-1/2 -translate-x-1/2 z-10">
                      <Badge className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-4 py-1 text-sm font-bold">
                        En Popüler
                      </Badge>
                    </div>
                  )}

                  <div className={`absolute inset-0 bg-gradient-to-br ${pkg.color} opacity-0 hover:opacity-10 transition-opacity duration-500 pointer-events-none`}></div>

                  <CardHeader className="space-y-4">
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${pkg.color} flex items-center justify-center transform transition-all duration-500`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <div className="space-y-2">
                      <CardTitle className="text-lg font-bold text-gray-900">{pkg.name}</CardTitle>
                      <p className="text-sm text-gray-600 leading-relaxed">{pkg.subtitle || pkg.description}</p>
                    </div>
                    <div>
                      <div className="text-3xl font-bold text-gray-900">
                        {pkg.price && pkg.price > 0
                          ? `₺${formatPriceWithoutCents(pkg.price)} / ay`
                          : 'Özel Teklif'}
                      </div>
                    </div>
                  </CardHeader>

                  <CardContent className="flex flex-col flex-1">
                    <div className="space-y-2 flex-1">
                      {!isEnterprise && features.map((feature, idx) => (
                        <div key={idx} className="flex items-start gap-2">
                          <CheckCircle2 className="w-4 h-4 text-indigo-500 mt-0.5 flex-shrink-0" />
                          <span className="text-sm text-gray-600 leading-relaxed">{feature}</span>
                        </div>
                      ))}
                      
                      {/* Comparison Features kaldırıldı - Sadece pricing_plans'dan gelen özellikler gösteriliyor */}
                    </div>
                    <div className="pt-5">
                      <Button 
                        onClick={() => {
                          // Paketin URL'si varsa ona yönlendir, yoksa iletişim sayfasına
                          if (pkg.url) {
                            window.open(pkg.url, '_blank');
                          } else {
                            window.location.href = '/iletisim';
                          }
                        }}
                        className="w-full bg-gradient-to-r from-indigo-600 to-indigo-500 hover:from-indigo-700 hover:to-indigo-600 text-white"
                      >
                        {!pkg.price || pkg.price <= 0 ? 'Bilgi Al' : 'Paketi Satın Al'}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Audit Section */}
      <section
        className="py-16"
        style={{ opacity: ready ? 1 : 0, transition: 'opacity 0.6s ease-in 0.15s' }}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="flex flex-col justify-center h-full text-center lg:text-left">
              <div className="flex justify-center lg:inline-flex">
                <Badge className="bg-indigo-100 text-indigo-700">
                  {auditSection.badge_text || 'Marka Analizi'}
                </Badge>
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mt-4 mb-4">
                {auditSection.title || 'İlk adım: Kapsamlı marka analizi ve strateji geliştirme'}
              </h2>
              <p className="text-gray-600 text-lg leading-relaxed">
                {auditSection.description || 'Mevcut marka durumunuzu analiz ederek, hedef kitlenize uygun güçlü bir görsel kimlik oluşturmak için stratejik bir yol haritası sunuyoruz.'}
              </p>
              <ul className="mt-6 space-y-3 mx-auto lg:mx-0 max-w-md lg:max-w-none">
                {auditChecklist.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3 justify-start">
                    <CheckCircle2 className="w-5 h-5 text-indigo-600 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="grid sm:grid-cols-2 gap-6">
              {seoPillars.map((pillar, idx) => (
                <Card key={idx} className="border-indigo-200 shadow-sm hover:shadow-md transition-all">
                  <CardContent className="p-6 space-y-4">
                    <div>{iconMap[pillar.icon] || iconMap.Gauge}</div>
                    <h3 className="text-lg font-semibold text-gray-900">{pillar.title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{pillar.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Roadmap */}
      <section
        className="py-16 bg-white"
        style={{ opacity: ready ? 1 : 0, transition: 'opacity 0.6s ease-in 0.25s' }}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row items-center lg:items-start gap-12">
            <div className="w-full lg:w-2/5 flex justify-center hidden lg:flex">
              <div className="relative w-full max-w-xs">
                <div className="absolute -inset-4 bg-indigo-200 blur-3xl rounded-full"></div>
                <MediaDisplay
                  src={roadmapSection.image_url || 'https://images.unsplash.com/photo-1515378791036-0648a3ef77b2?auto=format&fit=crop&w=700&q=80'}
                  alt="Tasarım çalışma süreci"
                  className="relative w-full aspect-[3/4] rounded-3xl shadow-xl object-cover border border-indigo-100"
                  videoProps={{ controls: true }}
                  imgProps={{ loading: "lazy" }}
                />
              </div>
            </div>
            <div className="w-full lg:w-3/5 text-center lg:text-left">
              {/* Mobile Image - Above Badge */}
              <div className="w-full flex justify-center mb-6 lg:hidden">
                <div className="relative w-full max-w-xs">
                  <div className="absolute -inset-4 bg-indigo-200 blur-3xl rounded-full"></div>
                  <MediaDisplay
                    src={roadmapSection.image_url || 'https://images.unsplash.com/photo-1515378791036-0648a3ef77b2?auto=format&fit=crop&w=700&q=80'}
                    alt="Tasarım çalışma süreci"
                    className="relative w-full aspect-[3/4] rounded-3xl shadow-xl object-cover border border-indigo-100"
                    videoProps={{ controls: true }}
                    imgProps={{ loading: "lazy" }}
                  />
                </div>
              </div>
              <div className="mb-8 space-y-3">
                <div>
                  <Badge className="bg-indigo-100 text-indigo-700">
                    {roadmapSection.badge_text || 'Çalışma Planı'}
                  </Badge>
                </div>
                <h2 className="text-3xl font-bold text-gray-900">
                  {roadmapSection.title || '8 haftada marka kimliğinizi oluşturuyoruz'}
                </h2>
                <p className="text-gray-600">
                  {roadmapSection.description || 'İlk görüşmeden teslimata kadar tüm tasarım sürecini şeffaf şekilde planlıyor, markanızın hikayesini görselleştiriyoruz.'}
                </p>
              </div>
              <div className="grid gap-6 md:grid-cols-2">
                {roadmap.map((step, idx) => (
                  <Card key={idx} className="border border-indigo-200 h-full">
                    <CardContent className="p-6 space-y-4">
                      <div className="inline-flex items-center px-3 py-1 bg-indigo-50 text-indigo-600 text-xs font-semibold rounded-full">
                        <CalendarDays className="w-3.5 h-3.5 mr-1" />
                        {step.phase}
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900">{step.title}</h3>
                      <ul className="space-y-2 text-sm text-gray-600">
                        {(step.points || []).map((point, pidx) => (
                          <li key={pidx} className="flex items-start gap-2">
                            <span className="mt-1 h-1.5 w-1.5 rounded-full bg-indigo-400"></span>
                            <span>{point}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Case Study */}
      <section
        className="py-16"
        style={{ opacity: ready ? 1 : 0, transition: 'opacity 0.6s ease-in 0.35s' }}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-10 items-center flex-col-reverse lg:grid">
            <div className="w-full">
              <div className="relative w-full max-w-lg mx-auto lg:ml-auto">
                <div className="absolute -inset-4 bg-indigo-100 blur-3xl rounded-full"></div>
                <MediaDisplay
                  src={caseStudy.image_url || 'https://images.unsplash.com/photo-1525182008055-f88b95ff7980?auto=format&fit=crop&w=900&q=80'}
                  alt="Marka tasarım vaka çalışması"
                  className="relative w-full rounded-3xl shadow-xl object-cover border border-indigo-100"
                  videoProps={{ controls: true }}
                  imgProps={{ loading: "lazy" }}
                />
              </div>
            </div>
            <div className="text-center lg:text-right">
              <div className="inline-flex">
                <Badge className="bg-indigo-100 text-indigo-700">Vaka Çalışması</Badge>
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mt-4 mb-4">
                {caseStudy.brand || 'Teknoloji Şirketi - Kurumsal Kimlik'}
              </h2>
              <div className="space-y-4 text-gray-600">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-1">Sorun</h3>
                  <p className="text-center lg:text-right">
                    {caseStudy.challenge || 'Eski ve tutarsız görsel kimlik, marka değerlerini yansıtmayan tasarımlar ve profesyonel görünüm eksikliği.'}
                  </p>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-1">Yaklaşımımız</h3>
                  <p className="text-center lg:text-right">
                    {caseStudy.solution || 'Kapsamlı marka analizi, modern ve tutarlı kurumsal kimlik tasarımı, marka kılavuzu oluşturma ve tüm görsel materyallerin yenilenmesi.'}
                  </p>
                </div>
              </div>
              <div className="mt-6 grid sm:grid-cols-3 gap-4">
                {(caseStudy.impact || []).map((item, idx) => (
                  <div key={idx} className="rounded-2xl bg-white shadow-md border border-indigo-100 p-6 text-center">
                    <div className="text-2xl font-bold text-indigo-600">{item.metric}</div>
                    <p className="mt-2 text-sm text-gray-600">{item.label}</p>
                  </div>
                ))}
              </div>
              <div className="mt-6 flex justify-center lg:justify-end">
                <Button
                  variant="outline"
                  className="border-indigo-200 text-indigo-600 hover:bg-indigo-50"
                  onClick={() => window.open('/blog', '_blank')}
                >
                  Detaylı Vaka İncelemesi
                  <ExternalLink className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Deliverables */}
      <section
        className="py-16 bg-indigo-900"
        style={{ opacity: ready ? 1 : 0, transition: 'opacity 0.6s ease-in 0.4s' }}
      >
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-white">
          <div className="text-center max-w-3xl mx-auto">
            <Badge className="bg-white/20 text-white">
              {deliverablesSection.badge_text || 'Teslimatlar'}
            </Badge>
            <h2 className="text-3xl font-bold mt-4">
              {deliverablesSection.title || 'SEO iş ortaklığımızdan neler elde edeceksiniz?'}
            </h2>
            <p className="text-indigo-100 mt-3">
              {deliverablesSection.description || 'Marka tasarım sürecinde tüm çıktıları profesyonel formatta teslim ediyor, marka kılavuzu ve tasarım dosyalarını hazırlıyoruz.'}
            </p>
          </div>
          <div className="mt-10 grid sm:grid-cols-2 gap-6">
            {deliverables.map((item, idx) => (
              <div key={idx} className="flex items-start gap-3">
                <CheckCircle2 className="w-6 h-6 text-white mt-0.5" />
                <span className="text-sm text-indigo-50 leading-relaxed">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SEO Blog Highlights */}
      {seoArticles.length > 0 && (
        <section
          className="py-16 bg-white"
          style={{ opacity: ready ? 1 : 0, transition: 'opacity 0.6s ease-in 0.43s' }}
        >
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 mb-10">
              <div>
                <Badge className="bg-indigo-100 text-indigo-700">Blog</Badge>
                <h2 className="text-3xl font-bold text-gray-900 mt-3">Marka tasarımı için ipuçları ve rehberler</h2>
                <p className="text-gray-600 mt-2 max-w-3xl">
                  Logo tasarımı, kurumsal kimlik ve marka stratejisi üzerine yayınladığımız rehberleri keşfedin.
                </p>
              </div>
              <Button
                variant="outline"
                className="border-indigo-200 text-indigo-600 hover:bg-indigo-50"
                onClick={() => window.open('/blog?category=tasarim', '_blank')}
              >
                Tüm Tasarım Yazılarını Gör
              </Button>
            </div>
            <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
              {seoArticles.map((article, idx) => {
                const hasValidImage = article.image && 
                  article.image !== 'https://images.unsplash.com/photo-1520607162513-77705c0f0d4a?auto=format&fit=crop&w=800&q=80' &&
                  !article.image.includes('placeholder');
                
                return (
                  <Card key={idx} className="overflow-hidden border-indigo-100 hover:shadow-lg transition-shadow">
                    {hasValidImage ? (
                      <div className="aspect-[16/9] bg-indigo-50 overflow-hidden">
                        <MediaDisplay
                          src={article.image}
                          alt={article.title}
                          className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                          videoProps={{ controls: true }}
                          imgProps={{
                            loading: "lazy",
                            onError: (e) => {
                              // If image fails to load, replace with placeholder
                              e.target.style.display = 'none';
                              e.target.parentElement.innerHTML = '<div class="w-full h-full bg-gradient-to-br from-indigo-500 to-indigo-700 flex items-center justify-center"><svg class="w-16 h-16 text-white/50" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg></div>';
                            }
                          }}
                        />
                      </div>
                    ) : (
                      <div className="aspect-[16/9] bg-gradient-to-br from-indigo-500 to-indigo-700 flex items-center justify-center">
                        <svg className="w-16 h-16 text-white/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                        </svg>
                      </div>
                    )}
                    <CardContent className="p-5 space-y-3">
                      <div className="inline-flex items-center px-3 py-1 bg-indigo-50 text-indigo-600 text-xs font-semibold rounded-full">
                        <BookOpen className="w-3.5 h-3.5 mr-1" />
                        {article.tag}
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900">{article.title}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed">{article.excerpt}</p>
                      <button
                        onClick={() => window.open(article.link, '_blank')}
                        className="inline-flex items-center text-sm font-semibold text-indigo-600 hover:text-indigo-700"
                      >
                        Yazıyı Oku
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* SEO FAQ */}
      {displayFaqs.length > 0 && (
        <section
          className="py-16 bg-indigo-50"
          style={{ opacity: ready ? 1 : 0, transition: 'opacity 0.6s ease-in 0.46s' }}
        >
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-10 text-center">
              <Badge className="bg-indigo-200 text-indigo-800">Sıkça Sorulan Sorular</Badge>
              <h2 className="text-3xl font-bold text-gray-900 mt-3">Marka tasarım hizmetlerimiz hakkında merak edilenler</h2>
              <p className="text-gray-600 mt-2">Tasarım süreci, teslimat süreleri ve fiyatlandırma ile ilgili sık sorulan soruları yanıtladık.</p>
            </div>
            <div className="space-y-4">
              {displayFaqs.map((faq, index) => (
                <details
                  key={index}
                  className="group rounded-2xl bg-white border border-indigo-100 px-6 py-5 shadow-sm"
                  open={index === 0}
                >
                  <summary className="flex items-center justify-between cursor-pointer list-none">
                    <div className="flex items-center text-left">
                      <HelpCircle className="w-5 h-5 text-indigo-500 mr-3" />
                      <span className="text-base font-semibold text-gray-900 group-open:text-indigo-600">
                        {faq.question}
                      </span>
                    </div>
                    <ArrowRight className="w-4 h-4 text-indigo-400 transition-transform duration-200 group-open:rotate-90" />
                  </summary>
                  <p className="mt-4 text-sm leading-relaxed text-gray-600">
                    {faq.answer}
                  </p>
                </details>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section
        className="py-20 bg-gradient-to-r from-indigo-600 to-indigo-700 text-white"
        style={{ opacity: ready ? 1 : 0, transition: 'opacity 0.6s ease-in 0.5s' }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-4xl font-bold mb-4">Marka Tasarımınız İçin Bize Ulaşın</h2>
            <p className="text-xl text-indigo-100 mb-8 max-w-3xl mx-auto">
              Ücretsiz marka analizi ile başlayalım, markanızın görsel kimliğini güçlendirmek için özel bir tasarım planı oluşturalım.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a
                href="/iletisim"
                className="inline-flex items-center px-8 py-4 bg-white text-indigo-700 rounded-lg hover:bg-indigo-100 font-semibold text-lg transition-all duration-300 transform hover:scale-105 shadow-lg"
              >
                <Mail className="w-5 h-5 mr-2" />
                Marka Analizi Başlat
              </a>
              <a
                href={whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-8 py-4 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-semibold text-lg transition-all duration-300 transform hover:scale-105 border-2 border-white shadow-lg"
              >
                <MessageSquare className="w-5 h-5 mr-2" />
                WhatsApp ile Görüş
              </a>
              <Button
                size="lg"
                className="h-auto min-h-[3.75rem] inline-flex items-center px-8 py-4 bg-[#7A5AFF] hover:bg-[#8b6bff] text-white rounded-lg font-semibold text-lg shadow-lg"
                onClick={() => setMeetingOpen(true)}
              >
                Toplantı Ayarla
              </Button>
            </div>
            <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                <Palette className="w-12 h-12 mx-auto mb-4" />
                <h3 className="font-semibold text-lg mb-2">Ücretsiz Marka Analizi</h3>
                <p className="text-indigo-100">Mevcut marka durumunuzun analizi ve iyileştirme önerileri</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                <Award className="w-12 h-12 mx-auto mb-4" />
                <h3 className="font-semibold text-lg mb-2">Profesyonel Tasarım</h3>
                <p className="text-indigo-100">Logo, kurumsal kimlik ve tüm görsel materyallerin tasarımı</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                <FileText className="w-12 h-12 mx-auto mb-4" />
                <h3 className="font-semibold text-lg mb-2">Marka Kılavuzu</h3>
                <p className="text-indigo-100">Marka kullanım kılavuzu ve tüm tasarım dosyalarının teslimi</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      <MeetingRequestDialog
        open={meetingOpen}
        onOpenChange={setMeetingOpen}
        context="brand_design"
      />
    </div>
  );
}

